import {useState, useEffect, useContext} from 'react';
import classNames from 'classnames/bind';

import {emptyGeoJSON} from "core/ui/mapping/map";
import Field from 'core/ui/forms/field';

import {known_chart_ids} from "hooks/useChartsStore";

import SessionContext from 'SessionContext'

import { Button, ButtonGroup } from 'reactstrap';

const person_type_labels = {
  'Prospect': 'Prospect',
  'Contact': 'Contact',
  'Customer': 'Customer',
};

const DEFAULT_PERSON_TYPE_COLORS = {
  'Prospect': '#B61E54',
  'Contact': '#6db29c',
  'Customer': '#4e76ba',
};

const DEFAULT_PERSON_TYPE_HEATMAP_COLOR_STOPS = {
  'Prospect': [
    0, "rgba(63,122,172,0)",
    0.1, "rgba(167,129,247,0.1)",
    0.2, "rgba(147,129,227,0.8)",
    0.4, "rgba(220,169,200,0.6)",
    0.6, "rgba(223,199,209,1)",
    0.8, "rgba(209,98,138,0.9)",
    1, "rgb(182,30,87)"
  ],
  'Contact': [
    0, "rgba(63,122,172,0)",
    0.1, "rgba(167,149,227,0.1)",
    0.2, "rgba(167,149,227,0.8)",
    0.4, "rgba(99,168,146,0.6)",
    0.6, "rgba(179,228,196,1)",
    0.8, "rgba(89,168,126,0.8)",
    1, "rgb(79,148,116)"
  ],
  'Customer': [
    0, "rgba(63,122,172,0)",
    0.1, "rgba(167,129,247,0.1)",
    0.2, "rgba(127,129,227,0.8)",
    0.4, "rgba(88,78,196,0.4)",
    0.6, "rgba(78,88,186,0.6)",
    0.8, "rgba(58,98,186,0.8)",
    1, "rgb(58,98,186)"
  ],
};

// Read from window.skin lazily (not at module-eval time, since it's populated
// asynchronously by init.js before App renders, but after this module loads).
function getPersonTypeColors() {
  return window.skin?.colors?.personTypeColors || DEFAULT_PERSON_TYPE_COLORS;
}

function getPersonTypeHeatmapColorStops() {
  return window.skin?.colors?.personTypeHeatmapColorStops || DEFAULT_PERSON_TYPE_HEATMAP_COLOR_STOPS;
}

const PeopleTypeLayers = (props) => {
  const context = useContext(SessionContext);
  const [layerState, setLayerState] = useState({Prospect: 'hex', Contact: 'hex', Customer: 'pts', mapLayersReady: false});

  useEffect(() => {
    const default_state = {Prospect: 'hex', Contact: 'hex', Customer: 'pts'};
    const restoreState = JSON.parse(localStorage.getItem('explorer:layers:person-type') || 'null') || default_state;
    setLayerState({...restoreState, mapLayersReady: false});
    updateCrossfilterStore(restoreState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initializeMapLayers();
    updateLayerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.chartsStore, props.map]);

  useEffect(() => {
    updateLayerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.map, layerState]);

  useEffect(() => {
    if (!layerState.mapLayersReady || !context.crossfilterStore) return;
    updateLayerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.crossfilterStore, layerState.mapLayersReady]);

  useEffect(() => {
    const chartsStore = context.chartsStore;
    if (!chartsStore.loaded) return;
    const person_type_chart = chartsStore.charts[known_chart_ids.person_type];
    if (!person_type_chart) return;
    const map = props.map;
    for (let val of Object.keys(person_type_chart.value_labels)) {
      const setting = layerState[val] === undefined ? 'pts' : layerState[val];
      for (let type of ['pts', 'heat', 'hex']) {
        const layer_id = `person_type_${val}_${type}`;
        if (!map.getSource(layer_id)) continue;
        map.setLayoutProperty(layer_id, 'visibility', setting === type ? 'visible' : 'none');
      }
    }
  }, [props.map, layerState, context.chartsStore]);

  function initializeMapLayers(){
    if (!props.map) return;
    const chartsStore = context.chartsStore
    const person_type_chart = chartsStore.charts[known_chart_ids.person_type];
    if (!person_type_chart) {
      setLayerState(prev => ({...prev, mapLayersReady: true}));
      return;
    }

    const map = props.map;
    const personTypeColors = getPersonTypeColors();
    const personTypeHeatmapColorStops = getPersonTypeHeatmapColorStops();
    for(let val of Object.keys(person_type_labels)){
      let layer_id = `person_type_${val}_pts`;
      if (!map.getSource(layer_id)) {
        map.addSource(layer_id, emptyGeoJSON());
        map.addLayer({
          'id': layer_id,
          'source': layer_id,
          'type': 'circle',
          'paint': {
            'circle-color': personTypeColors[val],
            'circle-radius': 2
          }
        });
      }
      
      layer_id = `person_type_${val}_hex`;
      if (!map.getSource(layer_id)) {
        map.addSource(layer_id, emptyGeoJSON());
        map.addLayer({
          'id': layer_id,
          'source': layer_id,
          'type': 'fill',
          'paint': {
            'fill-color': personTypeColors[val],
            'fill-opacity': [
              'interpolate',
              ['linear'],
              ['get', 'count'],
              0, 0,
              2000, 1
            ],
          },
        });
      }
      
      layer_id = `person_type_${val}_heat`;
      if (!map.getSource(layer_id)) {
        map.addSource(layer_id, emptyGeoJSON());
        map.addLayer({
          'id': layer_id,
          'source': layer_id,
          'type': 'heatmap',
          'paint': heatmap_config(personTypeHeatmapColorStops[val])
        });
      }
    }
    setLayerState(prev => ({...prev, mapLayersReady: true}));
  }

  function updateLayerData() {
    if (!props.map) return;
    const chartsStore = context.chartsStore
    const person_type_chart = chartsStore.charts[known_chart_ids.person_type];
    if (!person_type_chart) {
      return;
    }

    const map = props.map;

    const points_store = context.crossfilterStore.points;

    const h3_indexes_store = context.crossfilterStore.h3_indexes;

    for (const key of Object.keys(getPersonTypeColors())) {

      /////////////////////////////////////////////////////////////////////////////////////////////
      // update point data
      /////////////////////////////////////////////////////////////////////////////////////////////
      const points = points_store[key] || [];
      const point_layer_id = `person_type_${key}_pts`;
      map.getSource(point_layer_id)?.setData({
        "type": "FeatureCollection",
        "features": points.map((e) => ({
          "type": "Feature",
          "properties": e,
          "geometry": {
            "type": "Point",
            "coordinates": e.point
          }
        }))
      });

      /////////////////////////////////////////////////////////////////////////////////////////////
      // update hex data
      /////////////////////////////////////////////////////////////////////////////////////////////
      const h3_indexes = h3_indexes_store[key] || [];
      const hex_layer_id = `person_type_${key}_hex`;
      map.getSource(hex_layer_id)?.setData({
        "type": "FeatureCollection",
        "features": h3_indexes.map(e => {
          return {
            "type": "Feature",
            "properties": e,
            "geometry": {
              "type": "Polygon",
              "coordinates": e.boundary
            }
          };
        })
      });

      const hex_max_count = Math.max.apply(null, (h3_indexes||[]).map(e => e.count));
      map.setPaintProperty(hex_layer_id, 'fill-opacity',
          ["interpolate", ["linear"], ["get", "count"], 0, 0, hex_max_count > 0 ? hex_max_count : 1, .6]);

      /////////////////////////////////////////////////////////////////////////////////////////////
      // update heatmap data
      /////////////////////////////////////////////////////////////////////////////////////////////
      const heat_indexes = h3_indexes_store[key] || [];
      const heat_layer_id = `person_type_${key}_heat`;
      map.getSource(heat_layer_id)?.setData({
        "type": "FeatureCollection",
        "features": heat_indexes.map(e => {
          return {
            "type": "Feature",
            "properties": e,
            "geometry": {
              "type": "Point",
              "coordinates": e.point
            }
          };
        })
      });

      const heat_max_count = Math.max.apply(null,  (heat_indexes||[]).map(e => e.count));
      map.setPaintProperty(heat_layer_id, 'heatmap-weight',
          ["interpolate", ["linear"], ["get", "count"], 0, 0, heat_max_count > 0 ? heat_max_count : 1, 1]);

    }
  }

  function persistLayerState(newState){
    const {mapLayersReady: _, ...persistable} = newState;
    localStorage.setItem('explorer:layers:person-type', JSON.stringify(persistable));
  }

  function onChangeCheckbox(val, ev){
    ev.stopPropagation();
    const newState = {...layerState, [val]: ev.target.checked ? 'pts' : false};
    updateCrossfilterStore(newState);
    setLayerState(newState);
    persistLayerState(newState);
  }

  function onClickButton(val, btn, ev){
    ev.stopPropagation();
    const newState = {...layerState, [val]: layerState[val] === btn ? false : btn};
    updateCrossfilterStore(newState);
    setLayerState(newState);
    persistLayerState(newState);
  }

  function updateCrossfilterStore(state){
    const hex_layers = [],
        points_layers = [];

    Object.entries(state).forEach(([person_type, view_type])=> {
      if(Object.keys(getPersonTypeColors()).indexOf(person_type) > -1 && view_type !== false) {
        if (view_type === 'pts') {
          points_layers.push(person_type);
        } else {
          hex_layers.push(person_type);
        }
      }
    });
    // Only call once after building the arrays (was inside forEach causing 3x calls per type)
    context.crossfilterStore.setHexLayers(hex_layers);
    context.crossfilterStore.setPointLayers(points_layers);
  }

  const chartsStore = context.chartsStore

  if (!chartsStore.loaded) return null;

  const person_type_chart = chartsStore.loaded && chartsStore.charts[known_chart_ids.person_type];

  if (person_type_chart) {
    return (
        <>
          <h4>{person_type_chart.label}</h4>
          <ul>
            {Object.entries(person_type_chart.value_labels)
                .map(([val, label]) => {
                      const checked = layerState[val]; // === undefined ? 'pts' : layerState[val];

                      return (
                          <li key={val}>
                            <Field label={label}>
                              <input type='checkbox' checked={!!checked}
                                    onChange={(ev) => onChangeCheckbox(val, ev)} />
                            </Field>
                          <menu>
                            <ButtonGroup size="sm">
                              <Button size="sm" onClick={(ev) => onClickButton(val, 'pts', ev)}
                                      className={classNames(checked === 'pts' ? 'active': '')}
                                      style={{'--active-color': getPersonTypeColors()[val]}}>pts</Button>
                              <Button size="sm" onClick={(ev) => onClickButton(val, 'heat', ev)}
                                      className={checked === 'heat' ? 'active': ''}
                                      style={{'--active-color': getPersonTypeColors()[val]}}>heat</Button>
                              <Button size="sm" onClick={(ev) => onClickButton(val, 'hex', ev)}
                                      className={checked === 'hex' ? 'active': ''}
                                      style={{'--active-color': getPersonTypeColors()[val]}}>hex</Button>
                            </ButtonGroup>
                            </menu>
                          </li>
                      );
                    }
                )}
          </ul>
        </>
    );
  }
  else {
    return (
      <div></div>
    )
  }
}
export default PeopleTypeLayers;

function heatmap_config(color_stops){
  return {
    // Increase the heatmap weight based on clustered count
    "heatmap-weight": ["get", "count"],
    // Increase the heatmap color weight weight by zoom level
    // heatmap-intensity is a multiplier on top of heatmap-weight
    "heatmap-intensity": [
      "interpolate",
      ["linear"],
      ["zoom"],
      0, 1,
      3, 2,
      6, 3,
      9, 4,
      12, 5
    ],
    // Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
    // Begin color ramp at 0-stop with a 0-transparancy color
    // to create a blur-like effect.
    "heatmap-color": [
      "interpolate",
      ["linear"],
      ["heatmap-density"],
      ...color_stops
    ],
    // Adjust the heatmap radius by zoom level
    "heatmap-radius": [
      "interpolate",
      ["linear"],
      ["zoom"],
        0, 10,
        4.5, 30,
        6.99999, 90,
        7, 40,
        8, 90,
        8.2, 60,
        9.5, 90,
        9.7, 45,
        11, 60,
        11.2, 60
    ],
    // Transition from heatmap to circle layer by zoom level
    "heatmap-opacity": 0.6,
  };
}
